import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, Filter, Sparkles, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Leakage as LeakageApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR, formatDate } from '@/lib/format';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as ReTooltip, Cell } from 'recharts';

const severityStyles = {
  high: { badge: 'text-rose-500 border-rose-500/30 bg-rose-500/5', label: 'High' },
  medium: { badge: 'text-amber-500 border-amber-500/30 bg-amber-500/5', label: 'Medium' },
  low: { badge: 'text-sky-500 border-sky-500/30 bg-sky-500/5', label: 'Low' },
};

export default function Leakage() {
  const { user, demoLogin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [severity, setSeverity] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      if (!user) await demoLogin();
      const data = await LeakageApi.mine();
      setEvents(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Phase 17 — refetch leakage whenever the user mutates cards or
  // transactions anywhere in the app. This is what makes "delete Magnus →
  // Leakage stops recommending Magnus" feel instant: any Cards.remove call
  // emits `rb:data-changed` and we refresh in place.
  useEffect(() => {
    const handler = (e) => {
      const ent = e?.detail?.entity;
      if (ent === 'card' || ent === 'transaction') load();
    };
    window.addEventListener('rb:data-changed', handler);
    return () => window.removeEventListener('rb:data-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => ['all', ...Array.from(new Set(events.map((l) => l.category)))], [events]);
  const filtered = useMemo(() => events.filter((l) => (category === 'all' || l.category === category) && (severity === 'all' || l.severity === severity)), [events, category, severity]);

  // ---- B2-real (UI side): split one-time milestone bonuses from recurring
  // rate gaps. Previously this page did `totalAnnual = totalMonth * 12`,
  // which multiplies a one-time welcome / anniversary milestone by 12 — a
  // double-count. Now we annualize only the recurring rate-gap portion and
  // add one-time bonuses ONCE on top.
  const totalObserved = events.reduce((s, l) => s + (l.value_inr || 0), 0); // raw, no projection
  const oneTimeBonuses = events.reduce((s, l) => s + (l.milestone_reward_inr || 0), 0);
  const recurringGap = Math.max(0, totalObserved - oneTimeBonuses);
  const totalAnnual = recurringGap * 12 + oneTimeBonuses; // honest projection
  const totalFix = Math.round(totalAnnual * 0.85);
  const byCategory = useMemo(() => {
    const map = {};
    events.forEach((l) => { map[l.category] = (map[l.category] || 0) + l.value_inr; });
    return Object.entries(map).map(([k, v]) => ({ category: k, value: v }));
  }, [events]);

  if (loading) return <div className="min-h-[40vh] grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <Card className="relative overflow-hidden p-6 sm:p-8 border-rose-500/20">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(800px circle at 20% 0%, rgba(244,63,94,0.10), transparent 60%)' }} />
        <div className="relative flex flex-col lg:flex-row gap-6 lg:items-end justify-between">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-rose-500/15 text-rose-500 grid place-items-center"><AlertTriangle className="h-4 w-4" /></div>
              <div className="text-xs uppercase tracking-[0.18em] text-rose-500 font-medium">Reward Leakage Center</div>
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">You&rsquo;re wasting <span className="text-rose-500">{formatINR(totalAnnual)}/year</span>.</h1>
            <p className="mt-2 text-sm text-muted-foreground">We&rsquo;ve identified <b className="text-foreground">{events.length}</b> leakage pattern{events.length === 1 ? '' : 's'}. Use the suggested cards below to recover up to <span className="text-emerald-500 font-medium">{formatINR(totalFix)}</span> annually.</p>
            {oneTimeBonuses > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid="leakage-attribution">
                Of which <b>{formatINR(recurringGap * 12)}</b> is recurring rate gap (annualised) and <b>{formatINR(oneTimeBonuses)}</b> are one-time milestone bonuses (counted once).
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 sm:min-w-[440px]">
            <Card className="p-3 bg-[hsl(var(--surface-2))] border-rose-500/20"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Observed</div><div className="mt-0.5 font-display text-lg font-semibold text-rose-500">{formatINR(totalObserved)}</div><div className="text-[9px] text-muted-foreground mt-0.5">across {events.length} txn{events.length === 1 ? '' : 's'}</div></Card>
            <Card className="p-3 bg-[hsl(var(--surface-2))] border-rose-500/20"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Recurring (annual)</div><div className="mt-0.5 font-display text-lg font-semibold">{formatINR(recurringGap * 12, { compact: true })}</div></Card>
            <Card className="p-3 bg-[hsl(var(--surface-2))] border-emerald-500/30"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Recoverable</div><div className="mt-0.5 font-display text-lg font-semibold text-emerald-500">{formatINR(totalFix, { compact: true })}</div></Card>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-semibold">Leakage by category</h3>
            <Badge variant="outline" className="gap-1 text-rose-500 border-rose-500/30 bg-rose-500/5"><TrendingDown className="h-3 w-3" /> {formatINR(totalObserved)} observed</Badge>
          </div>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byCategory} margin={{ left: 4, right: 12 }}>
                <XAxis dataKey="category" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => formatINR(v, { compact: true })} />
                <ReTooltip cursor={{ fill: 'hsl(var(--surface-2))' }} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12 }} formatter={(v) => formatINR(v)} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>{byCategory.map((entry, i) => <Cell key={i} fill="hsl(var(--chart-5))" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-muted-foreground" /><h3 className="font-display text-base font-semibold">Filters</h3></div>
          <div className="mt-4 space-y-3">
            <div><div className="text-xs text-muted-foreground mb-1">Category</div><Select value={category} onValueChange={setCategory}><SelectTrigger className="h-10"><SelectValue /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c === 'all' ? 'All categories' : c}</SelectItem>)}</SelectContent></Select></div>
            <div><div className="text-xs text-muted-foreground mb-1">Severity</div><Tabs value={severity} onValueChange={setSeverity}><TabsList className="w-full"><TabsTrigger value="all" className="flex-1">All</TabsTrigger><TabsTrigger value="high" className="flex-1">High</TabsTrigger><TabsTrigger value="medium" className="flex-1">Medium</TabsTrigger><TabsTrigger value="low" className="flex-1">Low</TabsTrigger></TabsList></Tabs></div>
          </div>
        </Card>
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold mb-3">Detected events</h2>
        <div className="space-y-3">
          {filtered.length === 0 ? <Card className="p-10 text-center text-sm text-muted-foreground">No events match your filters.</Card> :
            filtered.map((l, i) => {
              const s = severityStyles[l.severity];
              // B2: a leakage event can mix a one-time milestone bonus and a
              // recurring rate gap. We must NOT annualise the milestone half
              // (it only ever fires once). Split the value:
              //   recurring = value_inr - milestone_reward_inr (>= 0)
              //   one_time  = milestone_reward_inr
              // and project: annual = recurring * 12 + one_time.
              const oneTime = Math.max(0, l.milestone_reward_inr || 0);
              const recurring = Math.max(0, (l.value_inr || 0) - oneTime);
              const projected = recurring * 12 + oneTime;
              // If recurring is zero, this row is a pure one-shot milestone — surface
              // that explicitly so the user doesn't read "/year" for something that
              // fires once.
              const isPureOneTime = recurring < 1 && oneTime > 0;
              const projectedLabel = isPureOneTime ? 'One-time impact' : 'Annual leakage';
              return (
                <motion.div key={l.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-lg ${s.badge} grid place-items-center shrink-0 border`}><AlertTriangle className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap"><Badge variant="outline" className={s.badge}>{s.label}</Badge><Badge variant="outline">{l.category}</Badge>{l.merchant && <Badge variant="outline" className="text-muted-foreground">{l.merchant}</Badge>}{l.milestone_name && (<Badge variant="outline" className="gap-1 text-amber-600 border-amber-500/30 bg-amber-500/5" data-testid={`leakage-milestone-${l.id}`}><Sparkles className="h-3 w-3" />Hits &ldquo;{l.milestone_name}&rdquo;</Badge>)}<span className="text-[11px] text-muted-foreground">{formatDate(l.date)}</span></div>
                        <p className="mt-2 text-sm text-foreground/90 leading-relaxed">{l.title}</p>
                        <div className="mt-3 rounded-lg border border-border bg-[hsl(var(--surface-2))] p-3"><div className="flex items-start gap-2 text-xs"><Sparkles className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" /><span className="text-foreground/85"><span className="font-medium">Fix:</span> {l.fix}{l.suggested_card_name && <span className="text-muted-foreground"> &mdash; Recommended: {l.suggested_card_name}</span>}</span></div></div>
                      </div>
                      <div className="text-right shrink-0 hidden sm:block" data-testid={`leakage-event-projection-${l.id}`}>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{projectedLabel}</div>
                        <div className="font-display text-lg font-semibold text-rose-500">{formatINR(projected, { compact: true })}</div>
                        {oneTime > 0 && recurring > 0 && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground leading-tight">
                            {formatINR(recurring * 12, { compact: true })}/yr + {formatINR(oneTime, { compact: true })} once
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })
          }
        </div>
      </div>
    </div>
  );
}
